import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Trash2, MessageSquare, Loader2, Paperclip, FileText, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { ChatMessage } from './ChatMessage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Supported file types
const SUPPORTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
const SUPPORTED_DOC_TYPES = ['application/pdf', 'text/plain', 'text/csv', 'text/markdown', 'application/json', 'application/zip', 'application/x-zip-compressed'];
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export const ChatModal = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Extract base64 data (remove data:mime;base64, prefix)
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Handle file selection
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      // Check file size
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`File "${file.name}" exceeds ${MAX_FILE_SIZE_MB}MB limit`);
        continue;
      }
      
      // Check file type
      const isImage = SUPPORTED_IMAGE_TYPES.includes(file.type);
      const isDoc = SUPPORTED_DOC_TYPES.includes(file.type);
      
      if (!isImage && !isDoc) {
        toast.error(`File type "${file.type}" is not supported`);
        continue;
      }
      
      try {
        const base64 = await fileToBase64(file);
        const preview = isImage ? URL.createObjectURL(file) : null;
        
        setAttachments((prev) => [
          ...prev,
          {
            filename: file.name,
            mime_type: file.type,
            data: base64,
            preview,
            isImage,
          },
        ]);
        
        toast.success(`Added: ${file.name}`);
      } catch (err) {
        toast.error(`Failed to process: ${file.name}`);
      }
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove attachment
  const removeAttachment = (index) => {
    setAttachments((prev) => {
      const newAttachments = [...prev];
      // Revoke object URL if it exists
      if (newAttachments[index].preview) {
        URL.revokeObjectURL(newAttachments[index].preview);
      }
      newAttachments.splice(index, 1);
      return newAttachments;
    });
  };

  const handleSend = async () => {
    if ((!input.trim() && attachments.length === 0) || isLoading) return;

    // Build user message with attachments
    const userMessage = {
      role: 'user',
      content: input.trim() || (attachments.length > 0 ? 'Please analyze the attached files.' : ''),
      attachments: attachments.length > 0 ? attachments.map(a => ({
        filename: a.filename,
        mime_type: a.mime_type,
        isImage: a.isImage,
        preview: a.preview,
      })) : null,
    };
    
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setError(null);
    
    const hasImages = attachments.some(a => a.isImage);
    if (hasImages) {
      toast.info('🔄 Querying 4 AI models (vision-capable)...', { duration: 3000 });
    } else {
      toast.info('🔄 Querying 6 AI models...', { duration: 3000 });
    }

    try {
      // Build message history for API
      const apiMessages = [...messages, {
        role: 'user',
        content: userMessage.content,
        attachments: attachments.length > 0 ? attachments.map(a => ({
          filename: a.filename,
          mime_type: a.mime_type,
          data: a.data,
        })) : null,
      }].map((m) => ({
        role: m.role,
        content: m.content,
        attachments: m.attachments && m.attachments.length > 0 ? m.attachments.map(a => ({
          filename: a.filename,
          mime_type: a.mime_type,
          data: a.data,
        })) : undefined,
      }));

      // Clear attachments for next message
      const currentAttachments = [...attachments];
      setAttachments([]);
      
      // Revoke all preview URLs
      currentAttachments.forEach(a => {
        if (a.preview) URL.revokeObjectURL(a.preview);
      });

      const response = await fetch(`${API_URL}/api/multi-llm/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: apiMessages,
          best_of: 6,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      const assistantMessage = {
        role: 'assistant',
        content: data.answer,
        provider: data.winner_provider,
        model: data.winner_model,
        confidence: data.confidence,
        providerScores: data.provider_scores,
      };

      setMessages((prev) => [...prev, assistantMessage]);
      
      // Show success toast with winner info
      const providerNames = {
        openai: 'OpenAI GPT',
        anthropic: 'Claude',
        xai: 'Grok',
        deepseek: 'DeepSeek',
        gemini: 'Gemini',
        perplexity: 'Perplexity'
      };
      const winnerName = providerNames[data.winner_provider] || data.winner_provider;
      const respondedCount = data.provider_scores ? data.provider_scores.filter(p => p.responded).length : 0;
      toast.success(`✨ Best answer from ${winnerName} (${respondedCount} models responded)`);
    } catch (err) {
      console.error('Chat error:', err);
      setError(err.message || 'Failed to get response');
      toast.error('Failed to get response from AI models');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setError(null);
    setAttachments([]);
    toast.info('Chat cleared');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-3xl h-[80vh] mx-4 bg-[#0a0a0f] border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-white/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Multi-LLM ChatBot</h2>
              <p className="text-xs text-gray-400">Powered by 6 AI models • Supports images & documents</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            data-testid="close-chat-modal"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 flex items-center justify-center mb-4">
                <MessageSquare className="w-8 h-8 text-purple-400" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Start a Conversation</h3>
              <p className="text-sm text-gray-400 max-w-md">
                Ask about trading signals, crypto analysis, market trends, or any investment-related questions.
                You can also attach images or documents for analysis.
              </p>
              <div className="flex gap-2 mt-4 flex-wrap justify-center">
                {['OpenAI GPT', 'Claude', 'Grok', 'DeepSeek', 'Gemini', 'Perplexity'].map((name) => (
                  <span
                    key={name}
                    className="px-2 py-1 rounded-full text-xs bg-white/5 border border-white/10 text-gray-400"
                  >
                    {name}
                  </span>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-4">
                📎 Supports: PNG, JPG, GIF, WEBP, PDF, TXT, CSV, MD, JSON, ZIP (max {MAX_FILE_SIZE_MB}MB each)
              </p>
            </div>
          ) : (
            messages.map((msg, idx) => <ChatMessage key={idx} message={msg} />)
          )}

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-start mb-4">
              <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3">
                <div className="flex items-center gap-2 text-gray-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">
                    {attachments.some(a => a.isImage) 
                      ? 'Querying 4 models (vision)...' 
                      : 'Querying 6 models...'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="flex justify-center mb-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2 text-red-400 text-sm">
                {error}
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Attachment Preview Area */}
        {attachments.length > 0 && (
          <div className="px-6 py-3 border-t border-white/10 bg-white/5">
            <div className="flex gap-2 flex-wrap">
              {attachments.map((attachment, idx) => (
                <div
                  key={idx}
                  className="relative group flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                >
                  {attachment.isImage ? (
                    <>
                      <img
                        src={attachment.preview}
                        alt={attachment.filename}
                        className="w-10 h-10 object-cover rounded"
                      />
                      <span className="text-xs text-gray-300 max-w-[100px] truncate">
                        {attachment.filename}
                      </span>
                    </>
                  ) : (
                    <>
                      <FileText className="w-5 h-5 text-blue-400" />
                      <span className="text-xs text-gray-300 max-w-[100px] truncate">
                        {attachment.filename}
                      </span>
                    </>
                  )}
                  <button
                    onClick={() => removeAttachment(idx)}
                    className="p-1 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="px-6 py-4 border-t border-white/10 bg-white/5">
          <div className="flex gap-3">
            {/* File attachment button */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.txt,.csv,.md,.json,.zip"
              multiple
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading}
              className="px-3 py-3 bg-white/5 border border-white/10 rounded-xl text-gray-400 hover:bg-white/10 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Attach files"
              data-testid="attach-file-btn"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about signals, crypto, stocks... or attach files"
              className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 resize-none focus:outline-none focus:border-purple-500/50 transition-colors"
              rows={1}
              disabled={isLoading}
              data-testid="chat-input"
            />
            <button
              onClick={handleSend}
              disabled={(!input.trim() && attachments.length === 0) || isLoading}
              className="px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl text-white font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
              data-testid="send-message-btn"
            >
              <Send className="w-5 h-5" />
            </button>
            {messages.length > 0 && (
              <button
                onClick={clearChat}
                className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-gray-400 hover:bg-white/10 transition-colors"
                title="Clear chat"
                data-testid="clear-chat-btn"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-2 text-center">
            Press Enter to send • Shift+Enter for new line • 📎 to attach files
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChatModal;
